import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import HouseIcon from "@mui/icons-material/House";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import BookingsSidebar from "./BookingSidebar";
import BookingForm from "./BookingForm";
import PastTripCard from "./PastTripCard";
import { Avatar, Divider, Chip } from "@mui/material";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const getDateString = (daysFromToday = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return date.toISOString().split("T")[0];
};

export default function TravelBookingEntry() {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;
  const loggedUser = JSON.parse(
    localStorage.getItem("CapacitorStorage.user") || "{}",
  );
  const user_id = loggedUser?.id;
  const user_mobile = loggedUser?.mobile;
  const userDataObject = { user_id, user_mobile, store_id };

  const location = useLocation();
  const navigate = useNavigate();

  // ── RESPONSIVE ──────────────────────────────────────
  // Below `lg` the three panels can no longer sit side by side without being
  // unusably narrow, so we show one panel at a time behind a tab switcher.
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"), {
    noSsr: true,
  });
  const [activePane, setActivePane] = useState("form");

  console.log(location);

  const editBookingId = location?.state?.booking_id || null;
  const customerDetail = location?.state?.customer_data || null;

  // ── STATE ──────────────────────────────────────────
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [allVehicles, setAllVehicles] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedCustomerForPanel, setSelectedCustomerForPanel] =
    useState(null);
  const [selectedCustomerTrips, setSelectedCustomerTrips] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ── LOADING STATES ──
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true);

  // Reference to hold the BookingForm's itinerary handler
  const itineraryHandlerRef = useRef(null);
  const resetFormHandlerRef = useRef(null);
  const itineraryBookingsFetcherRef = useRef(null);

  // ← NEW: Reference to hold the BookingForm's customer-autofill handler
  const customerSelectHandlerRef = useRef(null);

  // ── COMPUTE OVERALL LOADING STATE ──
  const isInitialLoading =
    isLoadingBookings || isLoadingVehicles || isLoadingDrivers;

  // ── FETCH BOOKINGS ─────────────────────────────────
  const fetchBookings = useCallback(async () => {
    // if (!store_id) return;

    setIsLoadingBookings(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/travel/booking/store/${store_id}`,
        { params: { is_itinerary: "0" } },
      );
      if (response?.data?.success) {
        setBookings(response.data.data || []);
      }
    } catch (error) {
      console.error("Fetch bookings error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load bookings",
        severity: "error",
      });
    } finally {
      setIsLoadingBookings(false);
    }
  }, [store_id]);

  // ── INITIAL DATA FETCH ─────────────────────────────
  useEffect(() => {
    if (store_id === null || store_id === undefined) return;

    const fetchVehicles = async () => {
      setIsLoadingVehicles(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/travel/vehicles/store/${store_id}`,
        );
        if (response?.data?.success) {
          setAllVehicles(
            response.data.data.filter(
              (vehicle) => vehicle.vehicle_status === "A",
            ) || [],
          );
        } else {
          setSnackbar({
            open: true,
            message: "Failed to load vehicles",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message:
            error?.response?.data?.message || "Error while fetching vehicles",
          severity: "error",
        });
      } finally {
        setIsLoadingVehicles(false);
      }
    };

    const fetchDrivers = async () => {
      setIsLoadingDrivers(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/travel/drivers/store/${store_id}`,
        );
        if (response?.data?.success) {
          setAllDrivers(
            response.data.data.filter((driver) => driver.status !== "I") || [],
          );
        } else {
          setSnackbar({
            open: true,
            message: "Failed to load drivers",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message:
            error?.response?.data?.message || "Error while fetching drivers",
          severity: "error",
        });
      } finally {
        setIsLoadingDrivers(false);
      }
    };

    fetchVehicles();
    fetchDrivers();
    fetchBookings();
  }, [store_id, fetchBookings]);

  // ── ITINERARY USE HANDLER ──────────────────────────
  const handleItineraryUse = useCallback((itineraryData) => {
    if (itineraryHandlerRef.current) {
      itineraryHandlerRef.current(itineraryData);
    }
  }, []);

  const handleResetForm = useCallback(() => {
    if (resetFormHandlerRef.current) {
      resetFormHandlerRef.current();
    }
    setActivePane("form");
  }, []);

  // Picking a booking from the sidebar should reveal the form on small screens
  const handleSelectBooking = useCallback((booking) => {
    setSelectedBooking(booking);
    if (booking) setActivePane("form");
  }, []);

  const setResetFormHandler = useCallback((handler) => {
    resetFormHandlerRef.current = handler;
  }, []);

  // ── CALLBACK TO RECEIVE ITINERARY HANDLER FROM BOOKINGFORM ───
  const setItineraryHandler = useCallback((handler) => {
    itineraryHandlerRef.current = handler;
  }, []);

  // ← NEW: Customer select from sidebar → autofill BookingForm
  const handleCustomerSelect = useCallback((customerData) => {
    if (customerSelectHandlerRef.current) {
      customerSelectHandlerRef.current(customerData);
    }
    setActivePane("form");
  }, []);

  // ← NEW: Callback to receive customer-autofill handler from BookingForm
  const setCustomerSelectHandler = useCallback((handler) => {
    customerSelectHandlerRef.current = handler;
  }, []);

  const setItineraryBookingsFetcher = useCallback((fetcher) => {
    itineraryBookingsFetcherRef.current = fetcher;
  }, []);

  const handleGoToDashboard = () => navigate("/travel/dashboard");

  return (
    <>
      {/* =================== LOADING BACKDROP =================== */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={isInitialLoading}
      >
        <CircularProgress color="inherit" size={60} />
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Loading Booking System...
          </Typography>
        </Box>
      </Backdrop>

      {/* =================== MAIN CONTENT (rendered only after loading) =================== */}
      {!isInitialLoading && (
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: "#f5f5f5",
            // the app shell already pads the main area below md
            p: { xs: 0.5, md: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 1, lg: 2 },
              position: { xs: "sticky", lg: "static" },
              top: 0,
              zIndex: 11,
              bgcolor: "#f5f5f5",
              py: { xs: 0.75, lg: 0 },
            }}
          >
            <Box sx={{ display: "flex", gap: 1.4, alignItems: "center" }}>
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor: "#03b9d1",
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
              </Box>
              <Box
                onClick={() => navigate("/travel/dashboard")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor: "#03b9d1",
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <HouseIcon sx={{ fill: "#FFF" }} />
              </Box>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{ fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                Travel Booking Entry
              </Typography>
            </Box>
          </Box>

          {/* ===== PANE SWITCHER (mobile / tablet only) ===== */}
          {isCompact && (
            <Paper
              sx={{
                mb: 1,
                position: "sticky",
                top: 44,
                zIndex: 10,
                borderRadius: 1,
              }}
            >
              <Tabs
                value={activePane}
                onChange={(_, val) => setActivePane(val)}
                variant="fullWidth"
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": {
                    minHeight: 40,
                    fontSize: { xs: "0.7rem", sm: "0.78rem" },
                    fontWeight: 600,
                    textTransform: "none",
                    px: 0.5,
                    minWidth: 0,
                  },
                  "& .Mui-selected": { color: "#1976d2" },
                  "& .MuiTabs-indicator": { backgroundColor: "#1976d2" },
                }}
              >
                <Tab value="list" label="Trips & Customers" />
                <Tab value="form" label="Booking" />
                <Tab
                  value="assist"
                  label={
                    selectedCustomerTrips.length
                      ? `Assist (${selectedCustomerTrips.length})`
                      : "Assist"
                  }
                />
              </Tabs>
            </Paper>
          )}

          {/* Panes stay mounted and are hidden with CSS so that in-progress
              form input and list scroll position survive a tab switch. */}
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {/* =================== LEFT PANEL =================== */}
            <Grid
              item
              xs={12}
              lg={3}
              sx={{
                display: isCompact && activePane !== "list" ? "none" : "block",
              }}
            >
              <BookingsSidebar
                store_id={store_id}
                bookings={bookings}
                onNewBooking={handleResetForm}
                selectedBooking={selectedBooking}
                setSelectedBooking={handleSelectBooking}
                setSelectedCustomerForPanel={setSelectedCustomerForPanel}
                setSelectedCustomerTrips={setSelectedCustomerTrips}
                setSnackbar={setSnackbar}
                allVehicles={allVehicles}
                allDrivers={allDrivers}
                onItineraryUse={handleItineraryUse}
                onCustomerSelect={handleCustomerSelect}
                onItineraryBookingsFetcherReady={setItineraryBookingsFetcher}
              />
            </Grid>

            {/* =================== MIDDLE PANEL =================== */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: isCompact && activePane !== "form" ? "none" : "block",
              }}
            >
              <BookingForm
                store_id={store_id}
                onResetFormHandler={setResetFormHandler}
                user_id={user_id}
                editBookingId={editBookingId}
                customerDetail={customerDetail}
                selectedBooking={selectedBooking}
                setSelectedBooking={setSelectedBooking}
                bookings={bookings}
                fetchBookings={fetchBookings}
                allVehicles={allVehicles}
                allDrivers={allDrivers}
                setSnackbar={setSnackbar}
                navigate={navigate}
                location={location}
                userDataObject={userDataObject}
                setSelectedCustomerForPanel={setSelectedCustomerForPanel}
                setSelectedCustomerTrips={setSelectedCustomerTrips}
                onItineraryUseHandler={setItineraryHandler}
                onCustomerSelectHandler={setCustomerSelectHandler}
                onAfterSave={() => {
                  if (itineraryBookingsFetcherRef.current) {
                    itineraryBookingsFetcherRef.current();
                  }
                }}
              />
            </Grid>

            {/* =================== RIGHT PANEL =================== */}
            <Grid
              item
              xs={12}
              lg={3}
              sx={{
                display: isCompact && activePane !== "assist" ? "none" : "block",
              }}
            >
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  height: { xs: "auto", lg: "calc(100vh - 120px)" },
                  minHeight: { xs: 200, lg: 0 },
                  overflow: { xs: "visible", lg: "auto" },
                }}
              >
                {selectedCustomerForPanel ? (
                  <Box
                    sx={{
                      maxHeight: { xs: "none", lg: "calc(100vh - 160px)" },
                      overflow: { xs: "visible", lg: "auto" },
                      "&::-webkit-scrollbar": { width: 0, height: 0 },
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            backgroundColor: "#1976d2",
                            width: 32,
                            height: 32,
                            fontSize: "0.85rem",
                          }}
                        >
                          {(selectedCustomerForPanel.name ||
                            "?")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ lineHeight: 1.2 }}
                          >
                            {selectedCustomerForPanel.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedCustomerForPanel.mobile}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ mt: 1 }} />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1.5, color: "#333" }}
                    >
                      Past Trips
                      <Chip
                        label={selectedCustomerTrips.length}
                        size="small"
                        sx={{
                          ml: 1,
                          height: 18,
                          fontSize: "0.65rem",
                          backgroundColor: "#e3f2fd",
                          color: "#1565c0",
                        }}
                      />
                    </Typography>
                    {selectedCustomerTrips.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No past trips found
                        </Typography>
                      </Box>
                    ) : (
                      selectedCustomerTrips.map((trip, idx) => (
                        <PastTripCard
                          key={trip.bookingId || idx}
                          trip={trip}
                          index={idx}
                        />
                      ))
                    )}
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Smart Assist
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a customer from the Customers tab to view their
                      past trips here.
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* =================== SNACKBAR =================== */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </>
  );
}
